import { useCallback, useEffect, useState } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { MAX_GOAL, MIN_GOAL } from '../../constants';

import { saveShareGoal } from './api';

const SHARING_GOAL_STORAGE_KEY_PREFIX = 'sharing-goal-';
const DEFAULT_GOAL = 5;

function getStorageKey(petitionId: string): string {
	return `${SHARING_GOAL_STORAGE_KEY_PREFIX}${petitionId}`;
}

export function readStoredGoal(petitionId: string, errorReporter?: UtilityContext['errorReporter']): number {
	try {
		if (typeof window === 'undefined') return DEFAULT_GOAL;
		const raw = window.localStorage.getItem(getStorageKey(petitionId));
		if (raw == null) return DEFAULT_GOAL;
		const value = Number(raw);
		if (!Number.isInteger(value) || value < MIN_GOAL || value > MAX_GOAL) return DEFAULT_GOAL;
		return value;
	} catch (error) {
		void errorReporter?.report(new Error('Failed to read sharing goal from localStorage', { cause: error }));
		return DEFAULT_GOAL;
	}
}

function writeStoredGoal(petitionId: string, value: number, errorReporter?: UtilityContext['errorReporter']): void {
	try {
		if (typeof window === 'undefined') return;
		window.localStorage.setItem(getStorageKey(petitionId), String(value));
	} catch (error) {
		void errorReporter?.report(new Error('Failed to write sharing goal to localStorage', { cause: error }));
	}
}

type GoalMotivationEntry = {
	minGoal: number;
	emoji: string;
	message: string;
};

function getGoalMotivation(goal: number, config: GoalMotivationEntry[]) {
	let result = config[0];
	for (const entry of config) {
		if (goal >= entry.minGoal) result = entry;
	}
	return result;
}

type UseSetGoalStepOptions = {
	petitionId: string;
	userId: string | undefined;
	initialShareGoal: number | null;
};

export function useSetGoalStep({ petitionId, userId, initialShareGoal }: UseSetGoalStepOptions) {
	const { translate } = useI18n();
	const utilityContext = useUtilityContext();
	const { errorReporter } = utilityContext;
	const track = useTracking();

	const [goalValue, setGoalValueState] = useState(initialShareGoal ?? DEFAULT_GOAL);
	const [prevPetitionId, setPrevPetitionId] = useState(petitionId);
	const [prevInitialShareGoal, setPrevInitialShareGoal] = useState(initialShareGoal);

	// Render-phase state sync (React docs: "Adjusting state when a prop changes").
	// Without this, if SetGoalStep is reused across a petition switch (same instance, new petitionId),
	// goalValue would stay on the previous petition's value and saveGoal would PUT it under the new petitionId.
	if (petitionId !== prevPetitionId || initialShareGoal !== prevInitialShareGoal) {
		setPrevPetitionId(petitionId);
		setPrevInitialShareGoal(initialShareGoal);
		setGoalValueState(initialShareGoal ?? DEFAULT_GOAL);
	}

	// Keep initial render deterministic for SSR/hydration: read localStorage only post-mount.
	useEffect(() => {
		if (initialShareGoal != null) {
			writeStoredGoal(petitionId, initialShareGoal, errorReporter);
			return;
		}
		// eslint-disable-next-line react-hooks/set-state-in-effect -- localStorage is browser-only; reading during render would cause an SSR/hydration mismatch.
		setGoalValueState(readStoredGoal(petitionId, errorReporter));
	}, [petitionId, initialShareGoal, errorReporter]);

	const setGoalValue = useCallback(
		(value: number | ((prev: number) => number)) => {
			setGoalValueState((prev) => {
				const next = typeof value === 'function' ? value(prev) : value;
				writeStoredGoal(petitionId, next, errorReporter);
				return next;
			});
		},
		[petitionId, errorReporter],
	);

	const saveGoal = useCallback(() => {
		if (!userId) return;
		void saveShareGoal({ petitionId, goalValue }, utilityContext).catch(() => {
			void errorReporter.report(new Error('Failed to save sharing goal to server'));
		});
	}, [petitionId, userId, goalValue, errorReporter, utilityContext]);

	const goalMotivations: GoalMotivationEntry[] = [
		{ minGoal: 1, emoji: '🌱', message: translate('corgi.psf.share.sharing_goal.motivation_1') },
		{ minGoal: 5, emoji: '⭐', message: translate('corgi.psf.share.sharing_goal.motivation_5') },
		{ minGoal: 10, emoji: '💪', message: translate('corgi.psf.share.sharing_goal.motivation_10') },
		{ minGoal: 20, emoji: '🚀', message: translate('corgi.psf.share.sharing_goal.motivation_20') },
	];

	const { emoji, message } = getGoalMotivation(goalValue, goalMotivations);

	const trackSliderChange = useCallback(
		(value: number) => {
			void track('psf_share_goal_slider_change', { goal_value: value, petition_id: petitionId });
		},
		[track, petitionId],
	);

	const trackSetGoalClick = useCallback(
		(value: number) => {
			void track('psf_share_goal_set_click', { goal_value: value, petition_id: petitionId });
		},
		[track, petitionId],
	);

	return { goalValue, setGoalValue, saveGoal, emoji, message, trackSliderChange, trackSetGoalClick };
}
